"use client";

import { isNativePlatform } from "@/lib/issue-photo";

/**
 * Convert base64 data URL to Blob
 */
function dataURLtoBlob(dataURL: string): Blob {
  const parts = dataURL.split(",");
  const mimeMatch = parts[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/jpeg";
  const base64 = parts[1];
  const binary = atob(base64);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    array[i] = binary.charCodeAt(i);
  }
  return new Blob([array], { type: mime });
}

/**
 * Save image to device - triggers download on web, uses share on native
 * @param imageDataUrl - Base64 data URL of the image
 * @param fileName - Optional custom filename
 */
export async function saveImageToDevice(
  imageDataUrl: string,
  fileName?: string
): Promise<boolean> {
  const name = fileName || `issue-photo-${Date.now()}.jpg`;

  try {
    if (isNativePlatform()) {
      // On native, use share with "Save Image" option
      const blob = dataURLtoBlob(imageDataUrl);
      const file = new File([blob], name, { type: "image/jpeg" });

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Save Issue Photo",
        });
        console.log("[ImageViewer] Shared for save:", name);
        return true;
      }
    }

    // Web fallback - trigger download
    const blob = dataURLtoBlob(imageDataUrl);
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log("[ImageViewer] Downloaded:", name);
    return true;
  } catch (error) {
    // User cancelled share sheet or error occurred
    if ((error as Error).name === "AbortError") {
      console.log("[ImageViewer] Save cancelled by user");
      return false;
    }
    console.error("[ImageViewer] Failed to save image:", error);
    return false;
  }
}

/**
 * Share image using native share sheet or Web Share API
 * @param imageDataUrl - Base64 data URL of the image
 * @param title - Optional share title
 */
export async function shareImage(
  imageDataUrl: string,
  title?: string
): Promise<boolean> {
  const shareTitle = title || "FlexTraker Issue Photo";
  const fileName = `issue-photo-${Date.now()}.jpg`;

  try {
    const blob = dataURLtoBlob(imageDataUrl);
    const file = new File([blob], fileName, { type: "image/jpeg" });

    // Check if Web Share API with files is supported
    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      await navigator.share({
        files: [file],
        title: shareTitle,
        text: "Issue photo from FlexTraker",
      });
      console.log("[ImageViewer] Image shared successfully");
      return true;
    }

    // Fallback: Open image in new tab for manual save/share
    console.log("[ImageViewer] Share API not available, opening in new tab");
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    // Note: URL will be revoked when tab closes or on page unload

    return true;
  } catch (error) {
    // User cancelled share sheet
    if ((error as Error).name === "AbortError") {
      console.log("[ImageViewer] Share cancelled by user");
      return false;
    }
    console.error("[ImageViewer] Failed to share image:", error);
    return false;
  }
}
